import React, { FunctionComponent, ReactElement, useEffect, useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, useTheme } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import genericAccordionStyles from '../genericAccordion/genericAccordionStyles';
import clsx from 'clsx';
import scrollInView from '../../../../common/service/scrollInView';
import { ISpcTheme } from '../../../../common/theme/ISpcTheme';
import { ComplexAccordionRef } from '../../../hooks/useRefs';

export interface IComplexAccordionProps {
  /**
   * Component that gets render in the header (summary) of accordion
   */
  summary: ReactElement;
  /**
   * Component that rendered in content (details) of accordion
   */
  details: ReactElement;
  /**
   * String value used to modify accordion open state
   */
  setExpanded: (expanded: string) => void;
  /**
   * String value used to modify accordion open state
   */
  expandedPanelId: string;
  /**
   * Custom classes used to modify accordion style
   */
  classes?: {
    accordionPaper?: string;
    accordionSummary?: string;
    accordionDetails?: string;
    accordionSummaryContent?: string;
    expandIcon?: string;
    expandedIcon?: string;
    expandedSection?: string;
  };
  /**
   * Function for exposing ref object to upper levels
   */
  exposeRef?: (ref: ComplexAccordionRef) => void;
  dataTest?: string;
  id: number;
}

const ComplexAccordion: FunctionComponent<IComplexAccordionProps> = ({
  summary,
  details,
  classes,
  dataTest,
  id,
  expandedPanelId,
  exposeRef,
  setExpanded,
}) => {
  const styles = genericAccordionStyles();
  const accordionRef = useRef<HTMLDivElement>(null);
  const [activeAccordion, setActiveAccordionPanels] = useState([expandedPanelId]);
  const {
    spc: {
      customise: { showMultipleAccessoryOpenActivePanel },
    },
  } = useTheme<ISpcTheme>();

  const activeAccordionPanels = (panelId: string) => {
    if (activeAccordion.includes(panelId)) {
      setActiveAccordionPanels(activeAccordion.filter(panel => panel !== panelId));
    } else {
      setActiveAccordionPanels([...activeAccordion, panelId]);
    }
  };

  useEffect(() => {
    (accordionRef as ComplexAccordionRef).current['setExpanded'] = () => setExpanded(`panel_${id}`);
    exposeRef && exposeRef(accordionRef as ComplexAccordionRef);
  }, [exposeRef, accordionRef]);

  const handleChange = (panel: any) => (event: any, newExpanded: any) => {
    setExpanded(newExpanded ? panel : false);
    if (showMultipleAccessoryOpenActivePanel) activeAccordionPanels(panel);

    setTimeout(() => {
      if (!scrollInView.isInView(accordionRef.current)) {
        // because the header is sticky I needed an offset of 64px, could not use scrollIntoView
        window.scroll({
          top: accordionRef.current.getBoundingClientRect().top + window.scrollY - 64,
          behavior: 'smooth',
        });
      }
    }, 500);
  };

  return (
    <Accordion
      expanded={
        showMultipleAccessoryOpenActivePanel
          ? activeAccordion.includes(`panel_${id}`)
          : expandedPanelId === `panel_${id}`
      }
      onChange={handleChange(`panel_${id}`)}
      className={clsx([styles.accordionPaper, classes?.accordionPaper])}
      {...(dataTest ? { 'data-test': dataTest } : {})}
      ref={accordionRef}
    >
      <AccordionSummary
        className={clsx([styles.accordionSummary, classes?.accordionSummary])}
        classes={{
          expandIcon: clsx([classes?.expandIcon, { [classes?.expandedIcon]: expandedPanelId === `panel_${id}` }]),
          content: classes?.accordionSummaryContent,
          expanded: classes?.expandedSection,
        }}
        expandIcon={
          <ExpandMoreIcon
            className={styles.expandIcon}
            fontSize="small"
            {...(dataTest ? { 'data-test': `${dataTest}:expand_button` } : {})}
          />
        }
        {...(dataTest ? { 'data-test': `${dataTest}:summary` } : {})}
      >
        {summary}
      </AccordionSummary>
      <AccordionDetails className={clsx([styles.accordionDetails, classes?.accordionDetails])}>
        {details}
      </AccordionDetails>
    </Accordion>
  );
};

export default ComplexAccordion;
